import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
// Import images
import go_o from '../../Images/go_o.gif';
import go_d from '../../Images/go_d.gif';
import go_n from '../../Images/go_n.gif';
import save_d from '../../Images/save_d.gif';
import save_n from '../../Images/save_n.gif';
import btnattach_o from '../../Images/btnAttachment.png';
import print from '../../Images/print.gif';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const EL_Functions = {
  // Date Validation
  IsValidDate: (dateStr: string): boolean => {
    if (!dateStr || dateStr.length !== 10) return false;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return false;

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
    if (month < 1 || month > 12 || year < 1900) return false;

    const maxDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const leapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

    if (month === 2) {
      const febDays = leapYear ? 29 : 28;
      if (day > febDays) return false;
    } else if (day > maxDays[month - 1]) {
      return false;
    }

    return true;
  },

  IsValidTime: (timeStr: string): boolean => {
    if (!timeStr) return true;
    if (timeStr.length !== 5) return false;
    const parts = timeStr.split(':');
    if (parts.length !== 2) return false;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    return !isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
  },

  Trim: (str: string): string => str.replace(/^\s+|\s+$/g, ''),

  IsNumeric: (str: string, canHavePlusMinus: boolean = true, canHaveDot: boolean = true): boolean => {
    if (!str) return false;
    for (let i = 0; i < str.length; i++) {
      const ch = str.charAt(i);
      if (ch === '+' || ch === '-') {
        if (!canHavePlusMinus) return false;
      } else if (ch === '.') {
        if (!canHaveDot) return false;
      } else if (ch < '0' || ch > '9') {
        return false;
      }
    }
    return true;
  },

  ShowMessage: (message: string, type: string = 'A', title?: string, callback?: () => void): boolean => {
    if (type === 'Q') {
      const result = window.confirm(message);
      if (result && callback) callback();
      return result;
    } else {
      alert(message);
      if (callback) callback();
      return true;
    }
  },

  CheckNaN: (e: React.KeyboardEvent) => {
    const keyCode = e.keyCode || e.which;
    if (keyCode === 46) {
      e.preventDefault();
      return false;
    }
    if (keyCode < 48 || keyCode > 57) {
      if (keyCode !== 8 && keyCode !== 9 && keyCode !== 37 && keyCode !== 39) {
        e.preventDefault();
        return false;
      }
    }
    return true;
  },

  DateInput: (e: React.KeyboardEvent) => {
    const keyCode = e.keyCode || e.which;
    if ((keyCode < 47 || keyCode > 57) && keyCode !== 8 && keyCode !== 46) {
      e.preventDefault();
      return false;
    }
    return true;
  },

  TimeInput: (e: React.KeyboardEvent) => {
    const keyCode = e.keyCode || e.which;
    if ((keyCode < 48 || keyCode > 57) && keyCode !== 8 && keyCode !== 58) {
      e.preventDefault();
      return false;
    }
    return true;
  },

  FormatDate: (date: Date): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  },

  ParseDate: (dateStr: string): Date | null => {
    if (!EL_Functions.IsValidDate(dateStr)) return null;
    const parts = dateStr.split('/');
    return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
  },

  FormatTime: (date: Date): string => {
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes} ${ampm}`;
  },

  ParseTime: (timeStr: string): { hours: number, minutes: number, ampm: string } | null => {
    if (!timeStr) return null;
    const parts = timeStr.split(' ');
    const timeParts = parts[0].split(':');
    if (timeParts.length !== 2) return null;
    let hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    const ampm = parts[1] || 'AM';
    if (isNaN(hours) || isNaN(minutes)) return null;
    if (ampm === 'PM' && hours < 12) hours += 12;
    if (ampm === 'AM' && hours === 12) hours = 0;
    return { hours, minutes, ampm };
  },

  CombineDateTime: (dateStr: string, timeStr: string): string => {
    const date = EL_Functions.ParseDate(dateStr);
    const time = EL_Functions.ParseTime(timeStr);
    if (!date || !time) return '';
    date.setHours(time.hours, time.minutes);
    return date.toISOString();
  }
};

// =======================
// INTERFACES
// =======================
interface InstallationRow {
  DDCode: string;
  SchoolCode: string;
  FamilyCode: string;
  Login: string;
  Password: string;
  FamilyName: string;
  InstallationDate: string;
  InstallationTime: string;
  InstallationAMPM: string;
  InstallerLogin: string;
  HardLocks: string;
  DongleInputs: string[];
  OrderComments: string;
  InstallStatus: string;
  PackageCodes: string;
  PreAuthorisation: string;
  DateOfSale: string;
  OrderRef: string;
  TypeOfProduct: string;
  Country: string;
  DocumentName: string;
  City: string;
  PostalCode: string;
  ApprovedToDispatchOn: string;
}

interface Installer {
  Login: string;
  Name: string;
}

interface Package {
  PackageCode: string;
  PackageDescription: string;
}

// =======================
// MAIN COMPONENT
// =======================
const EL_DeliveryDockets: React.FC = () => {
  // Set background color
  useEffect(() => {
    document.body.style.backgroundColor = 'azure';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  // =======================
  // STATE MANAGEMENT
  // =======================

  // Form filters
  const [fromDate, setFromDate] = useState<Date>(new Date(new Date().setMonth(new Date().getMonth() - 3)));
  const [toDate, setToDate] = useState<Date>(new Date());
  const [installDate, setInstallDate] = useState('');
  const [installer, setInstaller] = useState('');
  const [surname, setSurname] = useState('');
  const [suburb, setSuburb] = useState('');
  const [zipCode, setZipCode] = useState('');

  // Data and UI state
  const [installationData, setInstallationData] = useState<InstallationRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [installStatusOptions, setInstallStatusOptions] = useState<string[]>([]);
  const [country, setCountry] = useState('');
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [installers, setInstallers] = useState<Installer[]>([]);
  const [showAttachmentDialog, setShowAttachmentDialog] = useState(false);
  const [attachmentParams, setAttachmentParams] = useState({
    schoolCode: '',
    familyCode: '',
    login: ''
  });

  // Refs for hidden fields
  const hidDDCode = useRef<HTMLInputElement>(null);
  const hidSchoolCode = useRef<HTMLInputElement>(null);
  const hidFamilyCode = useRef<HTMLInputElement>(null);
  const hidLogin = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();

  // =======================
  // API CALLS (RESTful)
  // =======================

  // Load installation data
  const loadInstallationData = async () => {
    setIsLoading(true);
    setErrorMsg('');

    try {
      const params = new URLSearchParams({
        from_date: fromDate.toISOString().split('T')[0],
        to_date: toDate.toISOString().split('T')[0],
        install_date: installDate,
        installer: installer,
        surname: surname,
        suburb: suburb,
        zip_code: zipCode,
        school_code: sessionStorage.getItem('school_code') || 'DLR_KE_AU',
        family_code: sessionStorage.getItem('family_code') || 'A',
        login: sessionStorage.getItem('login') || 'adminkeau'
      });

      const response = await fetch(`${API_BASE_URL}api/installations/?${params.toString()}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setInstallationData(data.data);
        setCountry(data.country);
        setInstallStatusOptions(data.status_options.split('^').slice(1));
      } else {
        setErrorMsg(data.error || 'Failed to load installation data');
      }
    } catch (error) {
      console.error('Error loading installation data:', error);
      setErrorMsg('Failed to load installation data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load installation status options
  const loadInstallationStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}api/status/installation/`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.options) {
        setInstallStatusOptions(data.options.split('^').slice(1));
      }
    } catch (error) {
      console.error('Error loading installation status:', error);
    }
  };

  // Load installers for a specific row
  const loadInstallers = async (schoolCode: string, familyCode: string, ddCode: string, currentInstaller: string = '') => {
    try {
      const response = await fetch(
        `${API_BASE_URL}api/installers/?school_code=${schoolCode}&family_code=${familyCode}&dd_code=${ddCode}&current_installer=${currentInstaller}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.html;
    } catch (error) {
      console.error('Error loading installers:', error);
      return "<select style='width: 120px'><option value=''>Error loading installers</option></select>";
    }
  };

  // Validate dongle number
  const validateDongleNumber = async (dongleNo: string, schoolCode: string, familyCode: string, login: string): Promise<boolean> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}api/dongles/validate/?number=${dongleNo}&school_code=${schoolCode}&family_code=${familyCode}&login=${login}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.valid) {
        EL_Functions.ShowMessage("Invalid Dongle number.", "A");
      }
      return data.valid;
    } catch (error) {
      console.error('Error validating dongle:', error);
      EL_Functions.ShowMessage("Error validating dongle number", "E");
      return false;
    }
  };

  // Get package names
  const getPackageNames = async (packageCodes: string): Promise<string> => {
    if (!packageCodes || packageCodes === 'NULL' || packageCodes.trim() === '') {
      return '';
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}api/packages/?codes=${encodeURIComponent(packageCodes)}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.names ? data.names.join('<br/>') : '';
    } catch (error) {
      console.error('Error getting package names:', error);
      return '';
    }
  };

  // Get document name
  const getDocumentName = async (schoolCode: string, familyCode: string, login: string): Promise<string> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}api/documents/?school_code=${schoolCode}&family_code=${familyCode}&login=${login}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.document_name || '';
    } catch (error) {
      console.error('Error getting document name:', error);
      return '';
    }
  };

  // Update installation
  const updateInstallation = async (payload: any) => {
    try {
      const response = await fetch(`${API_BASE_URL}api/installations/${payload.dd_code}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        EL_Functions.ShowMessage(data.error || 'Failed to update installation', 'E');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error updating installation:', error);
      EL_Functions.ShowMessage('Failed to update installation', 'E');
      return false;
    }
  };

  // Send installation
  const sendInstallation = async (ddCode: string, schoolCode: string, familyCode: string, login: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}api/installations/${ddCode}/send/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          school_code: schoolCode,
          family_code: familyCode,
          login: login
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        EL_Functions.ShowMessage(data.error || 'Failed to send package', 'E');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error sending package:', error);
      EL_Functions.ShowMessage('Failed to send package', 'E');
      return false;
    }
  };

  // Unapprove installation
  const unapproveInstallation = async (ddCode: string, schoolCode: string, familyCode: string, login: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}api/installations/${ddCode}/unapprove/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          school_code: schoolCode,
          family_code: familyCode,
          login: login
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        EL_Functions.ShowMessage(data.error || 'Failed to unapprove docket', 'E');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error unapproving docket:', error);
      EL_Functions.ShowMessage('Failed to unapprove docket', 'E');
      return false;
    }
  };

  // Delete document
  const deleteDocument = async (schoolCode: string, familyCode: string, login: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}api/documents/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          school_code: schoolCode,
          family_code: familyCode,
          login: login
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        EL_Functions.ShowMessage(data.error || 'Failed to delete document', 'E');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      EL_Functions.ShowMessage('Failed to delete document', 'E');
      return false;
    }
  };

  // Save document
  const saveDocument = async (payload: any) => {
    try {
      const response = await fetch(`${API_BASE_URL}api/documents/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        EL_Functions.ShowMessage(data.error || 'Failed to save document', 'E');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error saving document:', error);
      EL_Functions.ShowMessage('Failed to save document', 'E');
      return false;
    }
  };

  const handleGO = () => {
    if (installDate && !EL_Functions.IsValidDate(installDate)) {
      EL_Functions.ShowMessage('Invalid install Date', 'A');
      return;
    }
    loadInstallationData();
  };

  const handleRowSelect = (ddCode: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedRows(prev =>
      prev.includes(ddCode) ? prev.filter(code => code !== ddCode) : [...prev, ddCode]
    );
  };

  const handleDongleChange = (ddCode: string, index: number, value: string) => {
    setInstallationData(prev =>
      prev.map(row =>
        row.DDCode === ddCode
          ? {
            ...row,
            DongleInputs: row.DongleInputs.map((d, i) => i === index ? value : d),
            HardLocks: row.DongleInputs.map((d, i) => i === index ? value : d).join(',')
          }
          : row
      )
    );
  };

  const handleProductTypeChange = (ddCode: string, value: string) => {
    setInstallationData(prev =>
      prev.map(row =>
        row.DDCode === ddCode ? { ...row, TypeOfProduct: value } : row
      )
    );
  };

  const handleInstallStatusChange = (ddCode: string, value: string) => {
    setInstallationData(prev =>
      prev.map(row =>
        row.DDCode === ddCode ? { ...row, InstallStatus: value } : row
      )
    );
  };

  const handlePreAuthChange = (ddCode: string, value: string) => {
    setInstallationData(prev =>
      prev.map(row =>
        row.DDCode === ddCode ? { ...row, PreAuthorisation: value } : row
      )
    );
  };

  const handleInstallerChange = (ddCode: string, value: string) => {
    setInstallationData(prev =>
      prev.map(row =>
        row.DDCode === ddCode ? { ...row, InstallerLogin: value } : row
      )
    );
  };

  const handleDateChange = (ddCode: string, value: string) => {
    setInstallationData(prev =>
      prev.map(row =>
        row.DDCode === ddCode ? { ...row, InstallationDate: value } : row
      )
    );
  };

  const handleTimeChange = (ddCode: string, field: 'hours' | 'minutes', value: string) => {
    setInstallationData(prev =>
      prev.map(row => {
        if (row.DDCode !== ddCode) return row;

        const timeParts = row.InstallationTime.split(':');
        let hours = field === 'hours' ? value : timeParts[0];
        let minutes = field === 'minutes' ? value : timeParts[1] || '00';

        // Validate time
        const h = parseInt(hours, 10) || 0;
        const m = parseInt(minutes, 10) || 0;

        if (h < 0 || h > 23 || m < 0 || m > 59) {
          return row;
        }

        return {
          ...row,
          InstallationTime: `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`
        };
      })
    );
  };

  const handleAMPMChange = (ddCode: string, value: string) => {
    setInstallationData(prev =>
      prev.map(row =>
        row.DDCode === ddCode ? { ...row, InstallationAMPM: value } : row
      )
    );
  };

  const handleCommentsChange = (ddCode: string, value: string) => {
    setInstallationData(prev =>
      prev.map(row =>
        row.DDCode === ddCode ? { ...row, OrderComments: value } : row
      )
    );
  };

  const fnSend = async (ddCode: string, schoolCode: string, familyCode: string, login: string) => {
    const row = installationData.find(r => r.DDCode === ddCode);
    if (!row) return;

    // Validate installation date/time
    if (row.InstallationDate && !EL_Functions.IsValidDate(row.InstallationDate)) {
      EL_Functions.ShowMessage("Please enter valid date (dd/MM/yyyy)", "A");
      return;
    }

    if (row.InstallationTime && !EL_Functions.IsValidTime(row.InstallationTime)) {
      EL_Functions.ShowMessage("Please enter valid time (HH:MM)", "A");
      return;
    }

    // Validate dongles for non-online products
    if (row.TypeOfProduct !== 'O' && row.TypeOfProduct !== 'Online') {
      const emptyDongle = row.DongleInputs.some(d => !d);
      if (emptyDongle) {
        EL_Functions.ShowMessage('Please enter Dongle numbers', "A");
        return;
      }

      for (const dongle of row.DongleInputs) {
        if (dongle && !(await validateDongleNumber(dongle, schoolCode, familyCode, login))) {
          return;
        }
      }
    }

    // Show confirmation
    const msg = "Before sending the package, make sure you have taken a print of the delivery docket.\nIf you have already taken a print, click OK to send the package.";

    if (EL_Functions.ShowMessage(msg, 'Q')) {
      const success = await sendInstallation(ddCode, schoolCode, familyCode, login);
      if (success) {
        EL_Functions.ShowMessage('Package sent successfully', 'I');
        loadInstallationData();
      }
    }
  };

  const fn_UnApprove = async (ddCode: string, schoolCode: string, familyCode: string, login: string) => {
    const msg = "Do you want to UnApprove the Delivery Docket?";
    if (EL_Functions.ShowMessage(msg, 'Q')) {
      const success = await unapproveInstallation(ddCode, schoolCode, familyCode, login);
      if (success) {
        EL_Functions.ShowMessage('Docket unapproved successfully', 'I');
        loadInstallationData();
      }
    } else {
      const chkUnApprove = document.getElementById(`chkUnApprove${ddCode}`) as HTMLInputElement;
      if (chkUnApprove) chkUnApprove.checked = false;
    }
  };

  const fn_Update = async (ddCode: string, schoolCode: string, familyCode: string, login: string) => {
    const row = installationData.find(r => r.DDCode === ddCode);
    if (!row) return;

    // Validate dongles for non-online products
    if (row.TypeOfProduct !== 'O' && row.TypeOfProduct !== 'Online') {
      for (const dongle of row.DongleInputs) {
        if (dongle && !(await validateDongleNumber(dongle, schoolCode, familyCode, login))) {
          return;
        }
      }
    }

    // Prepare payload
    const combinedDateTime = row.InstallationDate && row.InstallationTime
      ? EL_Functions.CombineDateTime(row.InstallationDate, `${row.InstallationTime} ${row.InstallationAMPM}`)
      : null;

    const payload = {
      dd_code: ddCode,
      school_code: schoolCode,
      family_code: familyCode,
      login: login,
      hard_locks: row.HardLocks,
      install_status: row.InstallStatus,
      pre_auth: row.PreAuthorisation,
      installation_datetime: combinedDateTime,
      product_type: row.TypeOfProduct,
      installer: row.InstallerLogin,
      comments: row.OrderComments
    };

    const success = await updateInstallation(payload);
    if (success) {
      EL_Functions.ShowMessage('Docket updated successfully', 'I');
      loadInstallationData();
    }
  };

  const fnPrintPV = (
    ddCode: string,
    schoolCode: string,
    familyCode: string,
    login: string
  ) => {
    const url = `/SALES/Dockets/EL_Print_DeliveryDocketDetails?DDCode=${ddCode}&SchoolCode=${schoolCode}&FamilyCode=${familyCode}&Login=${login}`;
    
    navigate(url);
  };

  const fnAdd = (schoolCode: string, familyCode: string, login: string) => {
    navigate(`/SALES/SalesModify/EL_SalesModify?SchoolCode=${schoolCode}&FamilyCode=${familyCode}&Login=${login}&Previous=/SALES/Dockets/EL_DeliveryDockets.aspx`);
  };

  const fn_Attachment = (fileName: string, login: string) => {
    const row = installationData.find(r => r.Login === login);
    if (!row) return;

    setAttachmentParams({
      schoolCode: row.SchoolCode,
      familyCode: row.FamilyCode,
      login: row.Login
    });
    setShowAttachmentDialog(true);
  };

  const fn_Delete = async (schoolCode: string, familyCode: string, login: string) => {
    const msg = `Do you want to delete uploaded Delivery Docket for ${login}?`;
    if (EL_Functions.ShowMessage(msg, 'Q')) {
      const success = await deleteDocument(schoolCode, familyCode, login);
      if (success) {
        EL_Functions.ShowMessage('Docket deleted successfully', 'I');
        loadInstallationData();
      }
    }
  };

  const fn_GetSelectedGridValues = async () => {
    if (selectedRows.length === 0) {
      EL_Functions.ShowMessage('Please select row to save details', 'A');
      return false;
    }

    let allValid = true;

    for (const ddCode of selectedRows) {
      const row = installationData.find(r => r.DDCode === ddCode);
      if (!row) continue;

      // Validate installation date/time
      if (row.InstallationDate && !EL_Functions.IsValidDate(row.InstallationDate)) {
        EL_Functions.ShowMessage("Please enter valid date (dd/MM/yyyy)", "A");
        allValid = false;
        break;
      }

      if (row.InstallationTime && !EL_Functions.IsValidTime(row.InstallationTime)) {
        EL_Functions.ShowMessage("Please enter valid time (HH:MM)", "A");
        allValid = false;
        break;
      }

      // Validate dongles for non-online products
      if (row.TypeOfProduct !== 'O' && row.TypeOfProduct !== 'Online') {
        const emptyDongle = row.DongleInputs.some(d => !d);
        if (emptyDongle) {
          EL_Functions.ShowMessage('Please enter Dongle numbers', "A");
          allValid = false;
          break;
        }

        for (const dongle of row.DongleInputs) {
          if (dongle && !(await validateDongleNumber(dongle, row.SchoolCode, row.FamilyCode, row.Login))) {
            allValid = false;
            break;
          }
        }
      }

      if (!allValid) break;

      // Prepare payload for this row
      const combinedDateTime = row.InstallationDate && row.InstallationTime
        ? EL_Functions.CombineDateTime(row.InstallationDate, `${row.InstallationTime} ${row.InstallationAMPM}`)
        : null;

      const payload = {
        dd_code: ddCode,
        school_code: row.SchoolCode,
        family_code: row.FamilyCode,
        login: row.Login,
        installer: row.InstallerLogin,
        installation_datetime: combinedDateTime,
        comments: row.OrderComments,
        product_type: row.TypeOfProduct,
        install_status: row.InstallStatus,
        pre_auth: row.PreAuthorisation,
        hard_locks: row.HardLocks
      };

      const success = await updateInstallation(payload);
      if (!success) {
        allValid = false;
        break;
      }
    }

    if (allValid) {
      EL_Functions.ShowMessage('All selected installations updated successfully', 'I');
      loadInstallationData();
    }

    return allValid;
  };

  const handleAttachmentSave = () => {
    // In real app, would handle file upload
    EL_Functions.ShowMessage('Document attachment would be handled here', 'I');
    setShowAttachmentDialog(false);
  };

  useEffect(() => {
    loadInstallationStatus();
    loadInstallationData();
  }, []);

  // Load package names for all rows
  useEffect(() => {
    if (installationData.length > 0) {
      installationData.forEach(async (row) => {
        if (row.PackageCodes && row.PackageCodes !== 'NULL' && row.PackageCodes.trim() !== '') {
          try {
            const packageNames = await getPackageNames(row.PackageCodes);
            // Handle package names if needed
          } catch (error) {
            console.error(`Error loading package names for ${row.DDCode}:`, error);
          }
        }
      });
    }
  }, [installationData]);

  // Load document names for all rows
  useEffect(() => {
    if (installationData.length > 0) {
      installationData.forEach(async (row) => {
        const docName = await getDocumentName(row.SchoolCode, row.FamilyCode, row.Login);
        if (docName && row.DocumentName !== docName) {
          // In a real app, you would update the state with the document name
        }
      });
    }
  }, [installationData]);


  const styles = {
    container: {
      width: '100%',
      margin: '0 auto',
      padding: '5px',
      fontFamily: 'Arial',
      fontSize: '11pt'
    } as React.CSSProperties,

    table: {
      width: '100%',
      borderCollapse: 'collapse',
      border: '1px solid black'
    } as React.CSSProperties,

    tableCell: {
      border: '1px solid black',
      padding: '5px',
      verticalAlign: 'top'
    } as React.CSSProperties,

    tableCellNoBorder: {
      padding: '5px',
      verticalAlign: 'top'
    } as React.CSSProperties,

    input: {
      fontFamily: "'Trebuchet MS', sans-serif",
      fontSize: '10pt',
      border: '1px solid #ccc',
      padding: '2px'
    } as React.CSSProperties,

    button: {
      cursor: 'pointer',
      border: 'none',
      background: 'transparent'
    } as React.CSSProperties,

    goButton: {
      cursor: 'pointer'
    } as React.CSSProperties,

    saveButton: {
      cursor: 'pointer'
    } as React.CSSProperties,

    hidden: {
      display: 'none'
    } as React.CSSProperties,

    loading: {
      textAlign: 'center',
      padding: '20px',
      fontSize: '12pt'
    } as React.CSSProperties,

    dataTable: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '10px',
      fontSize: '11pt',
      fontFamily: 'Arial'
    } as React.CSSProperties,

    dataTableHeader: {
      backgroundColor: '#c7ddfd',
      fontWeight: 'normal' as const,
      textAlign: 'left' as const,
      padding: '5px',
      border: '1px solid #80a0ff',
      color: '#2859ac',
      fontSize: '13px'
    } as React.CSSProperties,

    dataTableCell: {
      padding: '5px',
      border: '1px solid #ddd',
      textAlign: 'left' as const
    } as React.CSSProperties,

    selectedRow: {
      backgroundColor: '#d4e6f1'
    } as React.CSSProperties,

    dongleInput: {
      width: '92px',
      marginBottom: '2px',
      fontFamily: "'Trebuchet MS', sans-serif",
      fontSize: '10pt',
      border: '1px solid #767676',
      padding: '2px',
      borderRadius: '3px'
    } as React.CSSProperties,

    textArea: {
      width: '100%',
      height: '40px',
      fontFamily: "'Trebuchet MS', sans-serif",
      fontSize: '10pt',
      border: '1px solid #767676',
      padding: '2px',
      borderRadius: '3px'
    } as React.CSSProperties,

    select: {
      fontFamily: "'Trebuchet MS', sans-serif",
      fontSize: '10pt',
      border: '1px solid #767676',
      padding: '2px',
      borderRadius: '3px'
    } as React.CSSProperties,

    attachmentDialog: {
      position: 'fixed' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'white',
      padding: '20px',
      border: '1px solid rgb(128, 160, 255)',
      boxShadow: '0 0 10px rgba(0,0,0,0.1)',
      zIndex: 1000,
      width: '400px'
    } as React.CSSProperties,

    dialogOverlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 999
    } as React.CSSProperties
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginRight: "1%", gap: "1%", fontWeight: "bold", fontSize: "13px", marginTop: "1%", fontFamily: "Arial" }}>
        <a href='/SUPPORT/DealerDashboard' style={{ ...styles.button, marginBottom: '10px' }}>Back To Menu </a>
        <a href='/SUPPORT/SupportLogin' style={{ marginBottom: '10px' }}>Logout </a>
      </div>

      <div style={styles.container}>
        {/* Hidden fields */}
        <input type="hidden" id="hidDDCode" ref={hidDDCode} />
        <input type="hidden" id="hidSchoolCode" ref={hidSchoolCode} />
        <input type="hidden" id="hidFamilyCode" ref={hidFamilyCode} />
        <input type="hidden" id="hidLogin" ref={hidLogin} />

        {/* Filter Table */}
        <table style={{ ...styles.table, width: "1000px", margin: '0 auto' }} >
          <tbody>
            <tr style={styles.hidden}>
              <td colSpan={8} align="center" style={styles.tableCell}>
                <button id="Button1" style={styles.hidden}>Button</button>
              </td>
            </tr>
            <tr>
              <td style={{ ...styles.tableCell, borderBottom: 'none', width: '300px' }} align="center">
                Date of Sale
              </td>
              <td style={{ ...styles.tableCell, borderLeft: 'none', width: '190px' }}>
                Surname
                <input
                  type="text"
                  id="txtSurname"
                  maxLength={30}
                  style={{ ...styles.input, width: '100px' }}
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                />
              </td>
              <td style={{ ...styles.tableCell, borderLeft: 'none', width: '190px' }}>
                Installer&nbsp;&nbsp;
                <input
                  type="text"
                  id="txtInstaller"
                  maxLength={50}
                  style={{ ...styles.input, width: '100px' }}
                  value={installer}
                  onChange={(e) => setInstaller(e.target.value)}
                />
              </td>
              <td style={{ ...styles.tableCell, borderLeft: 'none', width: '205px' }}>
                Install Date
                <input
                  type="text"
                  id="txtInstallDate"
                  style={{ ...styles.input, width: '100px' }}
                  value={installDate}
                  onChange={(e) => setInstallDate(e.target.value)}
                  onKeyPress={EL_Functions.DateInput}
                />
              </td>
              <td rowSpan={2} style={{ ...styles.tableCell, borderLeft: 'none' }}>
                <img
                  id="imgbtnGO"
                  src={go_n}
                  alt="GO"
                  style={styles.goButton}
                  onMouseOver={(e) => (e.currentTarget as HTMLImageElement).src = go_o}
                  onMouseOut={(e) => (e.currentTarget as HTMLImageElement).src = go_n}
                  onMouseDown={(e) => (e.currentTarget as HTMLImageElement).src = go_d}
                  onClick={handleGO}
                />
              </td>
            </tr>
            <tr>
              <td style={{ ...styles.tableCell, borderTop: 'none' }} align="center">
                From
                <Flatpickr
                  id="calFromDate"
                  value={fromDate}
                  onChange={([date]) => setFromDate(date || fromDate)}
                  options={{ dateFormat: "d/m/Y" }}
                  style={{ ...styles.input, width: '100px' }}
                />
                &nbsp; To
                <Flatpickr
                  id="calTo"
                  value={toDate}
                  onChange={([date]) => setToDate(date || toDate)}
                  options={{ dateFormat: "d/m/Y" }}
                  style={{ ...styles.input, width: '100px' }}
                />
              </td>
              <td style={{ ...styles.tableCell, borderBottom: '1px solid black', borderRight: '1px solid black' }}>
                Suburb&nbsp;&nbsp;
                <input
                  type="text"
                  id="txtSuburb"
                  maxLength={25}
                  style={{ ...styles.input, width: '100px' }}
                  value={suburb}
                  onChange={(e) => setSuburb(e.target.value)}
                />
              </td>
              <td style={{ ...styles.tableCell, borderBottom: '1px solid black', borderRight: '1px solid black' }}>
                ZipCode
                <input
                  type="text"
                  id="txtZipCode"
                  maxLength={50}
                  style={{ ...styles.input, width: '100px' }}
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                />
              </td>
              <td align="right" style={{ ...styles.tableCell, borderBottom: '1px solid black', borderRight: '1px solid black' }}>&nbsp;</td>
            </tr>
          </tbody>
        </table>

        {/* Installation Data Table */}
        <div style={{ width: '100%', overflow: 'auto', marginTop: '10px' }}>
          {isLoading ? (
            <div style={styles.loading}>Loading installation data...</div>
          ) : (
            <table style={styles.dataTable}>
              <thead>
                <tr>
                  <th style={styles.dataTableHeader}>Family Name</th>
                  <th style={styles.dataTableHeader}>Type</th>
                  <th style={styles.dataTableHeader}>Installation Date<br />Installation Time</th>
                  <th style={styles.dataTableHeader}>Installer</th>
                  <th style={styles.dataTableHeader}>Upload Completed<br />Delivery Docket</th>
                  <th style={styles.dataTableHeader}>Install Status</th>
                  <th style={styles.dataTableHeader}>Print<br />Dockets</th>
                  <th style={styles.dataTableHeader}>Purchase Order</th>
                  <th style={styles.dataTableHeader}>Package</th>
                  <th style={styles.dataTableHeader}>Login</th>
                  <th style={styles.dataTableHeader}>Password</th>
                  <th style={styles.dataTableHeader}>Dongles</th>
                  <th style={styles.dataTableHeader}>Comments</th>
                  <th style={styles.dataTableHeader}>Pre<br />Authorisation</th>
                  <th style={styles.dataTableHeader}>DDCode</th>
                </tr>
              </thead>
              <tbody>
                {installationData.map((row, index) => (
                  <tr
                    key={row.DDCode}
                    style={{
                      backgroundColor: selectedRows.includes(row.DDCode)
                        ? 'orange' // selected row color
                        : index % 2 === 0
                          ? '#f4faff' // even (0,2,4...)
                          : '#ffffff', // odd (1,3,5...)

                    }}
                    onClick={(e) => handleRowSelect(row.DDCode, e)}
                  >


                    <td style={styles.dataTableCell}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const currentCountry = country.toUpperCase();

                          switch (currentCountry) {
                            case "CANADA":
                            case "CHINA":
                              fnSend(row.DDCode, row.SchoolCode, row.FamilyCode, row.Login);
                              break;
                            case "AUSTRALIA":
                              fnAdd(row.SchoolCode, row.FamilyCode, row.Login);
                              break;
                            default:
                              navigate(`/SALES/Dockets/EL_DeliveryDocketDetails?SchoolCode=${row.SchoolCode}&FamilyCode=${row.FamilyCode}&Login=${row.Login}&DDCode=${row.DDCode}&FromPage=Dockets`);
                          }
                        }}
                        style={{
                          color: 'blue',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontFamily: 'Arial',
                          fontSize: '10pt'
                        }}
                      >
                        {row.FamilyName}
                      </a>
                    </td>


                    <td style={styles.dataTableCell}>
                      {country.toUpperCase() === "CHINA" ? (
                        "Online"
                      ) : (
                        <select
                          id={`ddlProductType${row.DDCode}`}
                          style={{ ...styles.select, width: '135px' }}
                          value={row.TypeOfProduct || ''}
                          onChange={(e) => handleProductTypeChange(row.DDCode, e.target.value)}
                        >
                          <option value="">-Select-</option>
                          <option value="D">Dongle</option>
                          <option value="O">Online</option>
                          <option value="DO">Online And Dongle</option>
                        </select>
                      )}
                    </td>
                    <td style={styles.dataTableCell}>
                      <input
                        type="text"
                        id={`txtInstallationDate${row.DDCode}`}
                        value={row.InstallationDate || ''}
                        style={{ ...styles.input, width: '124px' }}
                        onChange={(e) => handleDateChange(row.DDCode, e.target.value)}
                        onKeyPress={EL_Functions.DateInput}
                      /><br />
                      <input
                        type="text"
                        id={`txtInstallationHH${row.DDCode}`}
                        value={row.InstallationTime ? row.InstallationTime.split(':')[0] : ''}
                        style={{ ...styles.input, width: '30px' }}
                        maxLength={2}
                        onChange={(e) => handleTimeChange(row.DDCode, 'hours', e.target.value)}
                        onKeyPress={EL_Functions.TimeInput}
                      />
                      :
                      <input
                        type="text"
                        id={`txtInstallationMM${row.DDCode}`}
                        value={row.InstallationTime ? row.InstallationTime.split(':')[1] : ''}
                        style={{ ...styles.input, width: '30px' }}
                        maxLength={2}
                        onChange={(e) => handleTimeChange(row.DDCode, 'minutes', e.target.value)}
                        onKeyPress={EL_Functions.TimeInput}
                      />
                      <select
                        id={`InstallationAMPM${row.DDCode}`}
                        style={{ ...styles.select, width: '50px' }}
                        value={row.InstallationAMPM || 'AM'}
                        onChange={(e) => handleAMPMChange(row.DDCode, e.target.value)}
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </td>
                    <td style={styles.dataTableCell}>
                      <div id={`installerContainer${row.DDCode}`}>
                        <select
                          style={{ ...styles.select, width: '120px' }}
                          value={row.InstallerLogin || ''}
                          onChange={(e) => handleInstallerChange(row.DDCode, e.target.value)}
                        >
                          <option value="">-select-</option>
                          {installStatusOptions.map((option, index) => {
                            const [text, value] = option.split(',');
                            return <option key={index} value={value}>{text}</option>;
                          })}
                        </select>
                      </div>
                    </td>
                    <td style={styles.dataTableCell}>
                      {row.DocumentName ? (
                        <>
                          <a href="#" onClick={(e) => { e.preventDefault(); fn_Attachment(row.DocumentName, row.Login); }}>
                            {row.DocumentName}
                          </a>
                          <br />
                          <button
                            style={{ ...styles.button, fontSize: '10px' }}
                            onClick={() => fn_Delete(row.SchoolCode, row.FamilyCode, row.Login)}
                          >
                            delete
                          </button>
                        </>
                      ) : (
                        <img
                          src={btnattach_o}
                          alt="Attachment"
                          style={{ cursor: 'pointer' }}
                          onClick={() => fn_Attachment('', row.Login)}
                        />
                      )}
                    </td>
                    <td style={styles.dataTableCell}>
                      <select
                        id={`ddlInstallStatus${row.DDCode}`}
                        style={{ ...styles.select, width: '100px' }}
                        value={row.InstallStatus || ''}
                        onChange={(e) => handleInstallStatusChange(row.DDCode, e.target.value)}
                      >
                        <option value="">-Select-</option>
                        {installStatusOptions.map((option, index) => {
                          const [text, value] = option.split(',');
                          return <option key={index} value={value}>{text}</option>;
                        })}
                      </select>
                    </td>
                    <td style={styles.dataTableCell}>
                      <img
                        src={print}
                        alt="Print"
                        style={{ cursor: 'pointer' }}
                        onClick={() => fnPrintPV(row.DDCode, row.SchoolCode, row.FamilyCode, row.Login)}
                      />
                    </td>
                    <td style={styles.dataTableCell}>
                      {row.OrderRef}<br />{row.DateOfSale}
                    </td>
                    <td style={styles.dataTableCell}>
                      {row.PackageCodes}
                    </td>
                    <td style={styles.dataTableCell}>{row.Login}</td>
                    <td style={styles.dataTableCell}>{row.Password}</td>
                    <td style={styles.dataTableCell}>
                      <table>
                        <tbody>
                          <tr>
                            <td>
                              {row.DongleInputs.map((dongle, i) => (
                                <React.Fragment key={i}>
                                  <input
                                    type="text"
                                    id={`txtHL${row.DDCode}${i}`}
                                    value={dongle || ''}
                                    style={styles.dongleInput}
                                    maxLength={6}
                                    onChange={(e) => handleDongleChange(row.DDCode, i, e.target.value)}
                                    onKeyPress={EL_Functions.CheckNaN}
                                  />
                                  <br />
                                </React.Fragment>
                              ))}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                    <td style={styles.dataTableCell}>
                      <textarea
                        id={`txtComments${row.DDCode}`}
                        style={styles.textArea}
                        value={row.OrderComments || ''}
                        onChange={(e) => handleCommentsChange(row.DDCode, e.target.value)}
                      />
                    </td>
                    <td style={styles.dataTableCell}>
                      <select
                        id={`ddlPreAuthorisation${row.DDCode}`}
                        style={{ ...styles.select, width: '80px' }}
                        value={row.PreAuthorisation || ''}
                        onChange={(e) => handlePreAuthChange(row.DDCode, e.target.value)}
                      >
                        <option value="">-Select-</option>
                        <option value="0">Declined</option>
                        <option value="1">Approved</option>
                      </select>
                    </td>
                    <td style={styles.dataTableCell}>{row.DDCode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Save Button */}
        <table style={{ ...styles.table, border: 'none', marginTop: '10px' }}>
          <tbody>
            <tr>
              <td align="center">
                <img
                  id="ImgSave"
                  src={save_n}
                  alt="Save"
                  style={styles.saveButton}
                  onMouseOver={(e) => (e.currentTarget as HTMLImageElement).src = save_d}
                  onMouseOut={(e) => (e.currentTarget as HTMLImageElement).src = save_n}
                  onMouseDown={(e) => (e.currentTarget as HTMLImageElement).src = save_n}
                  onClick={fn_GetSelectedGridValues}
                />
              </td>
            </tr>
          </tbody>
        </table>

        {/* Error Message */}
        {errorMsg && <div style={{ color: 'red', margin: '10px 0', fontWeight: 'bold' }}>{errorMsg}</div>}

        {/* Attachment Dialog */}
        {showAttachmentDialog && (
          <>
            <div style={styles.dialogOverlay} onClick={() => setShowAttachmentDialog(false)} />
            <div style={styles.attachmentDialog}>
              <h3>Attach Delivery Docket</h3>
              <p>School: {attachmentParams.schoolCode}</p>
              <p>Family: {attachmentParams.familyCode}</p>
              <p>Login: {attachmentParams.login}</p>
              <div style={{ marginTop: '20px' }}>
                <input type="file" style={{ marginBottom: '10px' }} />
                <div style={{ textAlign: 'right' }}>
                  <button
                    onClick={() => setShowAttachmentDialog(false)}
                    style={{ marginRight: '10px' }}
                  >
                    Cancel
                  </button>
                  <button onClick={handleAttachmentSave}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default EL_DeliveryDockets;